import { Box, Typography } from "@mui/material";
import { ArelTooltip } from "@arelia-components/ui-atoms/Tooltip";
import WidgetCard from "@arelia-components/ui-atoms/WidgetCard";
import { TopSongDTO } from "@arelia-types/song";
import { TopSongsPeriod } from "./models";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import theme from "../../theme";
import { ConnectionName, mapConnectionUIName } from "@arelia-types/connection";
import useRequiredConnections from "@arelia-utils/helpers/useRequiredConnections";
import WidgetMenuSelector from "@arelia-components/ui-atoms/WidgetMenuSelector";

type Props = {
  songs: TopSongDTO[];
  services: string[],
  loading: boolean;
  error: boolean;
  unconnected: boolean;
  selectedPeriod: TopSongsPeriod;
  handlePeriodChange: (value: TopSongsPeriod) => void;
};

export default function SongsPresenter({ songs, services, loading, error, unconnected, handlePeriodChange, selectedPeriod }: Props) {
  const isAmazonConnected = useRequiredConnections([ConnectionName.amazon]);

  const menuItems = [
    { label: 'All', value: TopSongsPeriod.AllTime },
    { label: '1 day', value: TopSongsPeriod.OneDay },
    { label: '7 days', value: TopSongsPeriod.SevenDays },
    { label: '28 days', value: TopSongsPeriod.TwentyEightDays }
  ];

  const renderPeriodSelector = () => (
    <Box sx={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
    }}>
      <WidgetMenuSelector<TopSongsPeriod>
        onClick={handlePeriodChange}
        selectedValue={selectedPeriod}
        menuItems={menuItems}
      />
      {
        isAmazonConnected && selectedPeriod === TopSongsPeriod.OneDay
          ? <ArelTooltip title="Data not available for Amazon">
            <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.secondary.main, cursor: "pointer" }} />
          </ArelTooltip>
          : <></>
      }
    </Box>
  );

  return (
    <WidgetCard
      widgetId="TOP_SONGS"
      title="Top Songs"
      headerComponent={renderPeriodSelector()}
      loading={loading}
      error={error}
      unconnected={unconnected}
    >
      <Box
        display="flex"
        flexDirection="column"
        flexGrow="1"
      >
        {
          songs.length > 0 ?
            songs.map(({ name, quantity, byService }, i) => (
              <Box
                key={i}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                marginBottom="14px"
              >
                <Typography variant="subtitle2" color="primary">
                  {i + 1}. {name}
                </Typography>
                <ArelTooltip title={services.map(name => (
                  <div key={name}>
                    <Typography variant="body2" component="span">
                      {mapConnectionUIName(name as ConnectionName)}: {byService[name] || 0}
                    </Typography>
                  </div>
                ))}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 500, cursor: "pointer" }}
                    color="secondary"
                  >
                    {quantity}
                  </Typography>
                </ArelTooltip>
              </Box>
            ))
            : 'No data available'
        }
      </Box>
    </WidgetCard>
  );
}
